import axios from 'axios';
import { appendAgentTypeToUrl } from 'app/utils/agentType';
import { header, clearCacheData, clearCookieData, handleLogout } from 'utils/auth';
import { ensureCsrfToken } from 'utils/csrf';
export class ResponseError extends Error {
  public response: Response;

  constructor(response: Response) {
    super(response.statusText);
    this.response = response;
  }
}
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
function parseJSON(response: Response) {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.json();
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
function checkStatus(response: Response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  console.log('response: ', response);


  const error = new ResponseError(response);
  error.response = response;
  throw error;
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export async function request(
  url: string,
  options?: any,
): Promise<{} | { err: ResponseError }> {
  let requestUrl = url;
  if (url.startsWith('/')) {
    requestUrl = `${process.env.REACT_APP_API_URL}${url}`;
  }

  // Inject globally-selected agent_type (if any) into the query string so
  // saga/fetch-based callers automatically participate in the filter.
  requestUrl = appendAgentTypeToUrl(requestUrl);
  const method = String(options?.method || 'GET').toUpperCase();
  const headers = new Headers(options?.headers || {});
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    try {
      const csrf = await ensureCsrfToken();
      if (csrf) headers.set('X-CSRF-Token', csrf);
    } catch (e) {
      console.warn('CSRF token fetch failed', e);
    }
  }
  const auth = header();
  if (auth) {
    headers.set('Authorization', auth);
  }

  try {
    const fetchResponse = await fetch(requestUrl, {
      ...options,
      credentials: 'include',
      headers,
    });
    // const response = checkStatus(fetchResponse);
    if (fetchResponse.status === 401) {
      handleLogout();
    }
    let parsedResponse: any = null;
    try {
      parsedResponse = await parseJSON(fetchResponse);
    } catch (e) {
      // If response body is empty/invalid JSON, still keep minimal info.
      parsedResponse = { statusCode: fetchResponse.status };
    }

    if (parsedResponse.statusCode === 401) {
      handleLogout();
    }
    if (parsedResponse.statusCode === 500) {
      throw new Error(parsedResponse?.message || "Server error occurred");
    }
    if (parsedResponse.message === 'Invalid credentials') {
      throw new Error('Invalid credentials. Please check your username and password.');
    }

    return parsedResponse;
  } catch (error: any) {
    console.log('error: ', error.response);
    if (error?.response?.data?.statusCode === 401) {
      handleLogout();
    }
    if (
      error?.response?.data?.message === 'Invalid credentials' ||
      error?.message === 'Invalid credentials'
    ) {
      throw new Error('Invalid credentials. Please check your username and password.');
    }
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    // Preserve API error message when we already threw with it (e.g. statusCode 500)
    if (error?.message) {
      throw error;
    }
    throw new Error("The myautoIQ server could not be reached. This may be due to a firewall in your environment. Please contact support@myautoiq.com for further assistance.");
  }
}

export async function requestAxios(
  url: string,
  options?: any,
): Promise<{} | { err: ResponseError }> {
  let requestUrl = url;
  if (url.startsWith('/')) {
    requestUrl = `${process.env.REACT_APP_API_URL}${url}`;
  }

  requestUrl = appendAgentTypeToUrl(requestUrl);
  const auth = header();
  const config: any = {
    headers: {
      'Content-Type': 'application/json',
      ...(auth ? { Authorization: auth } : {}),
    },
    withCredentials: true,
  };

  try {
    const response =
      options.method === 'GET'
        ? await axios.get(requestUrl, config)
        : await axios.post(requestUrl, {}, config);

    if (response?.status === 401 || response?.data?.statusCode === 401) {
      handleLogout();
    }
    if (
      response?.data?.statusCode === 400 &&
      (response?.data?.message?.includes('jwt expired') ||
        response?.data?.message?.includes('Access Denied'))
    ) {
      throw new Error(response?.data?.message || 'Access denied');
    }
    if (response?.data?.message === 'Invalid credentials') {
      throw new Error('Invalid credentials. Please check your username and password.');
    }
    if (response?.data?.statusCode === 500) {
      throw new Error(response.data.message || "Server error occurred");
    }

    return response?.data;
  } catch (error: any) {
    if (error?.response?.status === 401 || error?.response?.data?.statusCode === 401) {
      handleLogout();
    }
    if (
      error?.response?.data?.statusCode === 400 &&
      (error?.response?.data?.message?.includes('jwt expired') ||
        error?.response?.data?.message?.includes('Access Denied'))
    ) {
      throw new Error(error?.response?.data?.message || 'Access denied');
    }
    if (
      error?.response?.data?.message === 'Invalid credentials' ||
      error?.message === 'Invalid credentials'
    ) {
      throw new Error('Invalid credentials. Please check your username and password.');
    }
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error?.message) {
      throw error;
    }
    throw new Error("The myautoIQ server could not be reached. This may be due to a firewall in your environment. Please contact support@myautoiq.com for further assistance.");
  }
}
